import React, { useEffect, FC } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  getUserProfileClonesAsync,
  ITEMS_PER_PAGE,
} from "src/redux/users/actions";
import { selectorUserProfileClones } from "src/redux/users/selectors";
import { noProfileBannerTrns } from "src/page-components/users/user-profile/trns";
import { CloneCard } from "src/components/CloneCard";
import { Loader } from "src/components/Loader";
import { Pagination } from "src/components/Pagination";
import { NoResultsMessage } from "src/components/Error/NoResultsMessage";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { SORTING_OPTIONS } from "src/components/SortingDropdownList/constants";
import { scrollTop } from "src/utils/scrollTop";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { ISelectOption } from "src/@types/form";
import { Sizes } from "src/@types/sizes";
import cn from "classnames";
import { useExtractId } from "src/hooks/useExractId";

const UserProfileClones: FC = () => {
  const { t } = useTranslation();
  const id = useExtractId("id");

  const { getQueryParam, setQueryParam, setMultipleQueryParams } =
    useQueryParams();

  const dispatch = useAppDispatch();

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const setPage = (page: number) => {
    scrollTop();
    setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
  };

  const activeOption = SORTING_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_PARAM_KEYS.SORTING)
  );

  const setOption = ({ value }: ISelectOption) =>
    setMultipleQueryParams({
      [QUERY_PARAM_KEYS.SORTING]: value,
      [QUERY_PARAM_KEYS.PAGE]: 1,
    });

  const userProfileClones = useAppSelector(selectorUserProfileClones);
  const { isLoading, data } = userProfileClones || {};

  useEffect(() => {
    dispatch(
      getUserProfileClonesAsync({
        userId: id,
        page: currentPage,
        sorting: activeOption?.value,
      })
    );
  }, [dispatch, id, activeOption, currentPage]);

  if (isLoading) {
    return <Loader size={Sizes.L} className="self-center" />;
  } else if (!data) {
    return null;
  }

  const pageCount = Math.ceil(data?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  return (
    <div className="w-full">
      {data?.count > 0 ? (
        <div className="flex-1 lg:-mt-10">
          <div className="flex justify-end mb-3 gl:mb-0">
            <SortingDropdownList
              options={SORTING_OPTIONS}
              activeOption={activeOption || SORTING_OPTIONS[0]}
              setOption={setOption}
            />
          </div>

          <div
            className={cn("grid grid-cols-1 md:grid-cols-1 gap-3", {
              "sm:grid-cols-2": data?.results?.length > 1,
            })}
          >
            {Boolean(data?.results?.length) &&
              data.results.map((clone) => (
                <CloneCard key={clone.id} clone={clone} />
              ))}
          </div>

          {isPaginated && (
            <div className="flex items-center justify-center mt-5">
              <Pagination
                page={currentPage}
                pageCount={pageCount}
                setPage={setPage}
              />
            </div>
          )}
        </div>
      ) : (
        <NoResultsMessage className="self-start w-full">
          <h2 className="text-purple-base text-28">
            {t(noProfileBannerTrns.clonesTitle)}
          </h2>
        </NoResultsMessage>
      )}
    </div>
  );
};

export default UserProfileClones;
